//changed due to package upgrade
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  Grid,
  Stack,
  Button,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  DialogActions,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PropTypes from 'prop-types';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const formatRoleName = (roleName) => {
  if (!roleName) return ''; // Return empty string if undefined or null
  return roleName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const PermissionMatrixGrid = ({
  roles,
  modules,
  permissionTypes,
  permissions,
  rolePermissions,
  selectedRole,
  onBack,
  swapPermissions,
}) => {
  const companyTheme = CompanyTheme();
  const [openSwapDialog, setOpenSwapDialog] = useState(false);
  const isPermissionGranted = (roleId, permissionId) => {
    return rolePermissions[roleId]?.includes(permissionId) || false;
  };

  const getPermissionForModuleAndType = (moduleId, typeId) =>
    permissions.find(
      (p) => p?.module_id === moduleId && p?.permission_id === typeId,
    );

  const filteredModules = modules?.filter((module) => {
    return permissionTypes.some((type) => {
      const permission = getPermissionForModuleAndType(
        module?.module_id,
        type?.permission_id,
      );
      return (
        permission &&
        isPermissionGranted(selectedRole, permission?.module_permission_id)
      );
    });
  });

  return (
    <Box sx={{ width: '100%', px: 4, py: 3 }}>
      {/* Back Button & Heading */}
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        mb={4}
        flexWrap={{ xs: 'wrap', md: 'nowrap' }}
      >
        {/* Left Section (Back + Title) */}
        <Box display='flex' alignItems='center' gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            variant='outlined'
            size='medium'
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              borderColor: companyTheme ? companyTheme.primary.main : '#2196f3',
              '&:hover': {
                borderColor: companyTheme
                  ? alpha(companyTheme.primary.main, 0.8)
                  : alpha('#2196f3', 0.8),
              },
              color: companyTheme ? companyTheme.primary.main : 'text.primary',
            }}
          >
            Back
          </Button>

          <Typography
            variant='h4'
            fontWeight={700}
            sx={{
              color: companyTheme ? companyTheme.primary.main : 'text.primary',
            }}
          >
            Permissions for{' '}
            {formatRoleName(
              roles.find((r) => r.id === selectedRole)?.role_name,
            )}
          </Typography>
        </Box>

        {/* Right Section (Swap Access Button) */}
        {selectedRole && (
          <Button
            variant='outlined'
            size='small'
            onClick={() => setOpenSwapDialog(true)}
            sx={{
              borderRadius: 2,
              minWidth: { xs: 80, md: 90 },
              whiteSpace: 'nowrap',
              borderColor: companyTheme ? companyTheme.primary.main : '#2196f3',
              '&:hover': {
                borderColor: companyTheme
                  ? alpha(companyTheme.primary.main, 0.8)
                  : alpha('#2196f3', 0.8),
              },
              color: companyTheme ? companyTheme.primary.main : 'text.primary',
            }}
          >
            Swap Access
          </Button>
        )}
      </Box>

      {/* Two-Column Grid */}
      <Grid container spacing={3}>
        {filteredModules?.map((module) => (
          <Grid size={{ xs: 12,md: 6}} key={module?.id}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e0e0e0',
                background: '#fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                height: '100%',
              }}
            >
              <Typography
                variant='subtitle1'
                fontWeight={700}
                sx={{ mb: 1.5, fontSize: '1.1rem' }}
              >
                {module?.module_name}
              </Typography>

              <Grid container spacing={1} sx={{ mt: 1 }}>
                {permissionTypes.map((type) => {
                  const permission = getPermissionForModuleAndType(
                    module?.module_id,
                    type?.permission_id,
                  );
                  const isAvailable = !!permission;
                  const granted = isAvailable
                    ? isPermissionGranted(
                        selectedRole,
                        permission?.module_permission_id,
                      )
                    : false;

                  return (
                    <Grid  key={type.id}>
                      <Stack direction='row' alignItems='center'>
                        <Checkbox
                          checked={granted}
                          disabled
                          size='medium'
                          sx={{
                            p: 0,
                            mr: '-4px',
                            cursor: 'default',
                            '& .MuiSvgIcon-root': { fontSize: 20 },
                          }}
                        />
                        <Box
                          sx={{
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            opacity: isAvailable ? 1 : 0.4,
                            minWidth: 60,
                            textAlign: 'center',
                          }}
                        >
                          {type?.permission_name}
                        </Box>
                      </Stack>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
      {filteredModules?.length === 0 && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            mt: 8,
            mb: 10,
            px: 0,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              width: '100%',
              maxWidth: 900,
              p: 6,
              borderRadius: 4,
              mx: 'auto',
              background: '#fafbfd',
              border: '1px solid #dbe4f0',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography
              variant='h5'
              color='text.primary'
              sx={{
                fontWeight: 800,
                mb: 1,
                letterSpacing: '0.07em',
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              }}
            >
              <InfoOutlinedIcon
                sx={{ verticalAlign: 'middle', fontSize: 30, mr: 1 }}
              />
              No Permissions Assigned
            </Typography>
            {/* Decorative underline accent */}
            <Box
              sx={{
                width: 48,
                height: 4,
                bgcolor: companyTheme ? companyTheme.primary.main : '#3f51b5',
                borderRadius: 2,
                mb: 3,
                mx: 'auto',
              }}
            />
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{
                mb: 4,
                maxWidth: 650,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              This role currently has no permissions. To get started, assign
              permissions through the admin panel.
            </Typography>
            <Button
              variant='contained'
              size='medium'
              onClick={onBack}
              sx={{
                borderRadius: 50,
                px: 5,
                fontWeight: 600,
                backgroundColor: companyTheme
                  ? companyTheme.primary.main
                  : '#3f51b5',
                boxShadow: '0 4px 15px rgba(63,81,181,0.12)',
                '&:hover': {
                  backgroundColor: companyTheme
                    ? alpha(companyTheme.primary.main, 0.85)
                    : '#303f9f',
                  boxShadow: '0 6px 18px rgba(48,63,159,0.18)',
                },
                textTransform: 'none',
              }}
            >
              Go Back
            </Button>
          </Paper>
        </Box>
      )}
      {/* Role Swapping Section */}
      <Dialog
        open={openSwapDialog}
        onClose={() => setOpenSwapDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Typography
            variant='h4'
            fontWeight={700}
            sx={{
              color: companyTheme ? companyTheme.primary.main : 'text.primary',
            }}
          >
            Role Swapping Options
          </Typography>
        </DialogTitle>

        <DialogContent>
          {selectedRole && (
            <Box sx={{ py: 2 }}>
              <Typography sx={{ mb: 1 }}>
                Roles that{' '}
                <b>
                  {formatRoleName(
                    roles.find((r) => r.id === selectedRole)?.role_name,
                  )}
                </b>{' '}
                can swap to:
              </Typography>

              <FormControl fullWidth>
                <InputLabel sx={{ lineHeight: '1.5em' }}>
                  Roles this role can swap to
                </InputLabel>
                <Select
                  multiple
                  value={swapPermissions[selectedRole] || []}
                  input={
                    <OutlinedInput
                      sx={{ pt: 1 }}
                      label='Roles this role can swap to'
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => (
                        <Chip
                          key={id}
                          label={roles.find((r) => r.id === id)?.role_name}
                        />
                      ))}
                    </Box>
                  )}
                  disabled // 🔹 makes it read-only
                >
                  {roles
                    .filter((r) => r?.id !== selectedRole)
                    .map((role) => (
                      <MenuItem key={role?.id} value={role?.id} disabled>
                        <Checkbox
                          checked={
                            swapPermissions[selectedRole]?.includes(role?.id) ||
                            false
                          }
                        />
                        {role?.role_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenSwapDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionMatrixGrid;

PermissionMatrixGrid.propTypes = {
  roles: PropTypes.array.isRequired,
  modules: PropTypes.array.isRequired,
  permissionTypes: PropTypes.array.isRequired,
  permissions: PropTypes.array.isRequired,
  rolePermissions: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  selectedRole: PropTypes.object.isRequired,
  swapPermissions: PropTypes.func.isRequired,
};
